/**
 * ListBulkActions — fixed toolbar that slides in when ≥1 row is selected.
 *
 * Per the design notes: non-modal confirmation slide-overs handle the
 * "move to column" picker. Here we render the trigger buttons + count.
 * Parent wires the actual mutation (bulkApi.run).
 */
import { Archive, ChevronRight, X } from 'lucide-react';
import type * as React from 'react';

import { cn } from '../../../lib/utils';

export interface ListBulkActionsProps {
  selectedCount: number;
  onClear: () => void;
  onArchive?: (() => void) | undefined;
  onMove?: (() => void) | undefined;
  onAssignAgent?: (() => void) | undefined;
  onSetAutonomy?: (() => void) | undefined;
}

export function ListBulkActions({
  selectedCount,
  onClear,
  onArchive,
  onMove,
  onAssignAgent,
  onSetAutonomy,
}: ListBulkActionsProps) {
  if (selectedCount === 0) return null;
  return (
    <div
      className={cn(
        'flex items-center gap-1 border-b border-border bg-foreground/[0.04] px-3 py-1.5',
        'text-xs text-foreground',
      )}
      role="region"
      aria-label="Bulk actions"
    >
      <button
        type="button"
        onClick={onClear}
        aria-label="Clear selection"
        className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
      >
        <X size={12} aria-hidden />
      </button>
      <span className="ml-1 font-medium">{selectedCount} selected</span>
      <span className="ml-2 h-3 w-px bg-border" aria-hidden />
      {onMove && (
        <BulkButton onClick={onMove}>
          Move <ChevronRight size={10} aria-hidden />
        </BulkButton>
      )}
      {onAssignAgent && <BulkButton onClick={onAssignAgent}>Assign agent</BulkButton>}
      {onSetAutonomy && <BulkButton onClick={onSetAutonomy}>Set autonomy</BulkButton>}
      {onArchive && (
        <BulkButton onClick={onArchive}>
          <Archive size={11} aria-hidden /> Archive
        </BulkButton>
      )}
    </div>
  );
}

function BulkButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded px-2 py-1 transition-colors hover:bg-foreground/[0.06]"
    >
      {children}
    </button>
  );
}
